/**
 * The media_keys commit QUEUE — the serialization half of the canonical
 * merge-writer (mediaKeysWriter.ts is the pure deep-merge half).
 *
 * Why a queue (pre-merge review findings #1/#8): each commit's
 * refetch-before-write only serializes its OWN read against its OWN write.
 * Two rapid but distinct commits (placement, then "Reframe & Fit" aspect)
 * could still interleave — commit B's refetch resolving before commit A's
 * write lands means B merges against server state missing A's change, and
 * B's later write silently reverts A. postService.updatePost is
 * last-write-wins with no version check, so the loss is undetectable
 * client-side. The queue is a ref-held promise chain: commit N+1's run
 * (optimistic → refetch → merge → write) can never start before commit N's
 * run fully settles, success OR failure.
 *
 * Scope honesty: this QUEUE closes SAME-CLIENT races only. Task A4 (this
 * file's `mergeEntryViaRpc` path + `supabase/migrations/
 * 20260710250000_merge_post_media_keys_rpc.sql`) closes the CROSS-SESSION
 * race too, but ONLY for single-platform entry commits routed through the
 * RPC — see the RPC-vs-legacy eligibility comment at this file's
 * `mergeEntryViaRpc` call site. Two writers remain outside that RPC and can
 * still last-write-wins clobber each other (or an RPC writer) on this JSONB
 * column: (1) `transformKeys` commits (the archiver, platform attach/
 * detach/enable/disable — full-bag semantics the per-platform RPC can't
 * express) and (2) the publish cron's best-effort `cacheVariantUrl` write in
 * `supabase/functions/_shared/post-dispatcher.ts` (a plain unlocked
 * `.select` + JS merge + `.update`). Migrating both onto an atomic RPC path
 * is an A5-adjacent follow-up, not done here.
 *
 * Run algorithm (each numbered step maps to a review finding):
 *   1. Post-identity guard (#7): the post id captured at ENQUEUE time must
 *      still be the current post at run time — the Inspector reuses one
 *      PostBody instance across posts (openSubject swaps the subject), so a
 *      queued commit can outlive the post it was meant for. Mismatch →
 *      abort, no write, no revert (the post?.id effect already reset the
 *      override state for the new post; restoring the OLD post's snapshot
 *      onto it would corrupt the new post's UI).
 *   2. Optimistic application (#2): the revert snapshot is read INSIDE the
 *      serialized run — never at call time, where interleaved snapshots
 *      from overlapping commits would restore the wrong state. Entry thunks
 *      are resolved against the local snapshot for the optimistic pass
 *      (instant UI), then re-resolved against fresh server keys for the
 *      authoritative write.
 *   3. Refetch (#9): server truth is fetched inside the run. A failed or
 *      empty refetch ABORTS the commit (revert + failure result) — it never
 *      falls back to the render-closure snapshot this mechanism exists to
 *      distrust.
 *   4. Fresh resolution (#3): patch VALUES that depend on server state
 *      (framing's aspectRatio backfill) are thunks re-resolved against the
 *      REFETCHED keys, so a framing-only edit can't bake in a stale aspect
 *      and clobber a concurrent aspect write.
 *   5. Merge + write: pure deep-merge (mediaKeysWriter) of the resolved
 *      entries into fresh keys, then the optional top-level transform
 *      (disabledPlatforms sidecar, history[] — finding #4/#5 callers), then
 *      ONE updatePost carrying media_keys + any extraUpdates columns.
 *   6. Failure revert (#7 nuance): restore the snapshot ONLY if the current
 *      post is still the enqueue-time post.
 *
 * Framework-free on purpose — deps are plain functions so the whole race
 * surface is unit-testable with controlled promises, no React harness.
 */
import {
  type EntryPatch,
  type MediaKeysBag,
  type PerPlatformMap,
  applyPerPlatformPatches,
} from './mediaKeysWriter';

/**
 * One queued per-platform mutation. The platform is static (known at call
 * time — drives the per-platform saving indicator); the patch may be a thunk
 * resolved against the keys bag it's applied to (local snapshot for the
 * optimistic pass, refetched server keys for the write).
 */
export interface QueuedEntry {
  platform: string;
  patch: EntryPatch | ((keys: MediaKeysBag) => EntryPatch);
}

export interface QueuedCommitInput {
  /** Per-platform entry patches, deep-merged via mediaKeysWriter. */
  entries?: QueuedEntry[];
  /**
   * Top-level media_keys transform applied AFTER the entry patches, against
   * the same fresh-keys merge result — for writers that touch non-perPlatform
   * keys (the disabledPlatforms sidecar, history[]). Must be pure.
   */
  transformKeys?: (keys: MediaKeysBag) => MediaKeysBag;
  /**
   * Extra post-row columns written in the SAME updatePost call (e.g.
   * `platforms` when detaching a platform also clears its paused flag).
   * An input with ONLY extraUpdates skips the media_keys read/merge/write
   * entirely but still serializes through the queue.
   */
  extraUpdates?: Record<string, unknown>;
}

export type CommitFailureReason = 'post-changed' | 'refetch-failed' | 'write-failed';

export type CommitResult =
  { ok: true } | { ok: false; reason: CommitFailureReason; error?: unknown };

/**
 * Outcome of one `merge_post_media_keys` RPC call (Task A4). `missing`
 * distinguishes "this Postgres environment doesn't have the RPC yet" from a
 * real write failure — only the former triggers the legacy-path fallback; a
 * real failure still reverts the optimistic state like any other write
 * error. Callers (PostBody.tsx's `mergeEntryViaRpc` dep) set `missing` for
 * EXACTLY two error codes: PostgREST's schema-cache miss (`PGRST202` — the
 * shape actually returned over the wire when the RPC hasn't been added to
 * the schema cache yet) and the raw Postgres SQLSTATE (`42883`,
 * undefined_function, in case a caller ever sees the unwrapped Postgres
 * error). Any OTHER error (network failure, RLS/500, timeout) must NOT set
 * `missing` — it's a real failure and must revert + fail normally, never
 * silently pin the queue to the legacy path. A flat interface rather than a
 * discriminated union on purpose: this tsconfig runs `strict: false`, where
 * boolean-literal discriminant narrowing doesn't kick in on unions (the same
 * quirk PostBody.tsx documents at its `CommitResult` cast).
 */
export interface MergeEntryRpcOutcome {
  ok: boolean;
  /** True ONLY for PGRST202/42883 (RPC not deployed) — triggers the legacy fallback. */
  missing?: boolean;
  /** The underlying error for a real (non-missing) failure. */
  error?: unknown;
}

/**
 * The ONLY two error codes that mean "the `merge_post_media_keys` RPC isn't
 * deployed to this environment yet" — review finding P0 (queue-client
 * angle): PostgREST returns `PGRST202` (schema-cache miss) for a genuinely
 * missing RPC, not the raw Postgres SQLSTATE `42883` (undefined_function).
 * `42883` is kept as a defensive second case in case a caller ever observes
 * an unwrapped Postgres error. Exported + unit-tested directly (rather than
 * only exercised through a full PostBody.tsx render) because this is the
 * exact classification PostBody.tsx's `mergeEntryViaRpc` dep must get right:
 * any error code OTHER than these two is a real failure and must fall
 * through to normal revert-and-fail, never pin the queue to the legacy path.
 */
const MERGE_RPC_MISSING_ERROR_CODES = new Set(['PGRST202', '42883']);

/**
 * True only when `error.code` is one of `MERGE_RPC_MISSING_ERROR_CODES`.
 * Used by PostBody.tsx's `mergeEntryViaRpc` dep to decide `{ missing: true }`
 * vs. a real `{ error }` failure — see `MergeEntryRpcOutcome`'s docstring.
 */
export function isMergeRpcMissingError(error: { code?: string } | null | undefined): boolean {
  return !!error?.code && MERGE_RPC_MISSING_ERROR_CODES.has(error.code);
}

export interface CommitQueueDeps {
  /** The CURRENT post id (latest-ref read, not a render closure). */
  getCurrentPostId: () => string | null | undefined;
  /**
   * Fetch server truth for the post's media_keys. Return the bag ({} for a
   * row whose media_keys is legitimately null); return null/undefined (or
   * throw) when the refetch itself failed — the commit aborts rather than
   * trusting any cached/closure snapshot.
   */
  fetchFreshMediaKeys: () => Promise<MediaKeysBag | null | undefined>;
  /** Persist one updatePost call. Resolve { error: null } on success. */
  persistUpdate: (postId: string, updates: Record<string, unknown>) => Promise<{ error: unknown }>;
  /** Read the CURRENT optimistic per-platform override map (ref-backed). */
  readOverride: () => PerPlatformMap;
  /** Apply resolved entry patches to the local optimistic state. */
  applyOverridePatches: (patches: { platform: string; patch: EntryPatch }[]) => void;
  /** Restore the local optimistic state to a snapshot (failure revert). */
  restoreOverride: (snapshot: PerPlatformMap) => void;
  /** Refresh consumers after a successful write (post query invalidation). */
  afterSuccessfulWrite: () => Promise<unknown> | unknown;
  /**
   * Task A4: atomic per-platform merge via the `merge_post_media_keys` RPC.
   * OPTIONAL — omitting it (as every pre-A4 test harness does) keeps every
   * commit on the legacy client-computed whole-object write, byte-for-byte.
   * When present, SINGLE-platform entries-only commits (no `transformKeys`,
   * exactly one resolved entry) merge that platform server-side instead,
   * closing the cross-session clobber window the refetch-then-write pattern
   * couldn't. MULTI-platform commits ("apply to all") stay on the legacy
   * full-bag write on purpose — see the review-finding comment at this
   * RPC path's call site for why one atomic UPDATE beats N sequential RPC
   * commits for that case. See the `missing` branch on
   * `MergeEntryRpcOutcome` for the undeployed-migration fallback.
   */
  mergeEntryViaRpc?: (
    postId: string,
    platform: string,
    patch: EntryPatch,
  ) => Promise<MergeEntryRpcOutcome>;
}

export interface MediaKeysCommitQueue {
  enqueue: (input: QueuedCommitInput) => Promise<CommitResult>;
}

function resolveEntries(
  entries: QueuedEntry[],
  keys: MediaKeysBag,
): { platform: string; patch: EntryPatch }[] {
  return entries.map(({ platform, patch }) => ({
    platform,
    patch: typeof patch === 'function' ? patch(keys) : patch,
  }));
}

export function createMediaKeysCommitQueue(deps: CommitQueueDeps): MediaKeysCommitQueue {
  let chain: Promise<unknown> = Promise.resolve();
  // Task A4: feature-detection state for `mergeEntryViaRpc`, scoped to this
  // queue instance's lifetime (one per PostBody mount) — once the RPC
  // proves undeployed (`missing: true`) every subsequent commit skips
  // straight to the legacy write without re-probing. `loggedRpcFallback`
  // keeps the console.error to once per queue instance, not once per commit.
  let rpcUnavailable = false;
  let loggedRpcFallback = false;

  function enqueue(input: QueuedCommitInput): Promise<CommitResult> {
    const postIdAtEnqueue = deps.getCurrentPostId();

    const run = async (): Promise<CommitResult> => {
      if (!postIdAtEnqueue || deps.getCurrentPostId() !== postIdAtEnqueue) {
        return { ok: false, reason: 'post-changed' };
      }

      const entries = input.entries ?? [];
      const hasKeysWork = entries.length > 0 || !!input.transformKeys;
      const hasRowWork = !!input.extraUpdates && Object.keys(input.extraUpdates).length > 0;
      if (!hasKeysWork && !hasRowWork) return { ok: true };

      if (!hasKeysWork) {
        // Row-columns-only commit (e.g. attaching a platform) — no media_keys
        // read or write, but still serialized so it can't interleave with a
        // media_keys commit's refetch→write window.
        let writeError: unknown = null;
        try {
          const { error } = await deps.persistUpdate(postIdAtEnqueue, {
            ...input.extraUpdates,
          });
          writeError = error ?? null;
        } catch (error) {
          writeError = error;
        }
        if (writeError != null) return { ok: false, reason: 'write-failed', error: writeError };
        try {
          await deps.afterSuccessfulWrite();
        } catch {
          /* the write landed; consumers refresh on their next fetch */
        }
        return { ok: true };
      }

      // Optimistic pass — snapshot + application INSIDE the serialized run
      // (finding #2). Thunks resolve against the local snapshot here; the
      // authoritative write below re-resolves them against fresh server keys.
      const snapshot = deps.readOverride();
      const optimisticEntries = resolveEntries(entries, { perPlatform: snapshot });
      if (optimisticEntries.length > 0) deps.applyOverridePatches(optimisticEntries);

      const revertIfSamePost = () => {
        if (optimisticEntries.length > 0 && deps.getCurrentPostId() === postIdAtEnqueue) {
          deps.restoreOverride(snapshot);
        }
      };

      let freshKeys: MediaKeysBag | null | undefined;
      try {
        freshKeys = await deps.fetchFreshMediaKeys();
      } catch (error) {
        revertIfSamePost();
        return { ok: false, reason: 'refetch-failed', error };
      }
      if (freshKeys == null) {
        revertIfSamePost();
        return { ok: false, reason: 'refetch-failed' };
      }
      if (deps.getCurrentPostId() !== postIdAtEnqueue) {
        // Post switched during the refetch await — abort without writing and
        // WITHOUT reverting: the override state now belongs to the new post.
        return { ok: false, reason: 'post-changed' };
      }

      const resolvedEntries = resolveEntries(entries, freshKeys);

      // Task A4: atomic per-platform RPC path. Eligible only for SINGLE-
      // platform entries-only commits — no `transformKeys` (the
      // archiver/platform-toggle top-level transforms need full-bag
      // semantics the per-platform RPC can't express; this combination never
      // occurs today — see PostBody.tsx's call sites), exactly one resolved
      // entry, and only while the RPC hasn't been proven unavailable this
      // queue's lifetime.
      //
      // Review finding P1 (queue-client angle): a MULTI-platform commit
      // (e.g. "apply framing to all platforms") used to loop this RPC call
      // once per platform — N separate committed transactions. A real
      // failure partway through the loop left already-committed platforms
      // persisted server-side while the client reverted its optimistic
      // state for ALL platforms and reported total failure, a partial-write
      // hazard "apply to all" never had pre-A4 (one atomic updatePost).
      // Multi-platform commits now always fall through to the legacy
      // full-bag write below — ONE atomic UPDATE covering every target
      // platform, exactly like before Task A4 — and the RPC is reserved for
      // single-platform entry commits, where "atomic per-platform write" and
      // "atomic whole commit" are the same thing.
      //
      // `freshKeys` above already resolved every thunk (e.g. commitFraming's
      // aspectRatio backfill) against real server state, so `resolvedEntries`
      // here holds a plain, RPC-ready patch — the RPC's OWN internal
      // read-and-lock at write time (not this refetch) is what makes the
      // write atomic against a concurrent session, so `freshKeys` staleness
      // after this point is harmless.
      const mergeEntryViaRpc = deps.mergeEntryViaRpc;
      if (
        mergeEntryViaRpc &&
        !input.transformKeys &&
        !rpcUnavailable &&
        resolvedEntries.length === 1
      ) {
        const [{ platform, patch }] = resolvedEntries;
        const outcome = await mergeEntryViaRpc(postIdAtEnqueue, platform, patch);

        if (outcome.missing) {
          // Undeployed migration in this environment — stop trying the RPC
          // for the REST of this queue's lifetime and fall back to the
          // legacy whole-object write for this commit (and every commit
          // after it). Logged once, not per-commit.
          rpcUnavailable = true;
          if (!loggedRpcFallback) {
            loggedRpcFallback = true;
            console.error(
              '[mediaKeysCommitQueue] merge_post_media_keys RPC not found (PGRST202/42883) — falling back to the legacy media_keys write path for this session',
            );
          }
          // Fall through to the legacy path below. The RPC never wrote
          // (a missing function fails before mutating anything), so the
          // legacy whole-object write below is the first real write attempt.
        } else if (!outcome.ok) {
          revertIfSamePost();
          return { ok: false, reason: 'write-failed', error: outcome.error };
        } else {
          // Merged atomically server-side. `extraUpdates` alongside
          // `entries` never occurs today (verified against every
          // PostBody.tsx call site), but honor it defensively as a
          // row-columns-only follow-up write rather than silently dropping it.
          if (hasRowWork) {
            let rowWriteError: unknown = null;
            try {
              const { error } = await deps.persistUpdate(postIdAtEnqueue, {
                ...(input.extraUpdates ?? {}),
              });
              rowWriteError = error ?? null;
            } catch (error) {
              rowWriteError = error;
            }
            if (rowWriteError != null) {
              revertIfSamePost();
              return { ok: false, reason: 'write-failed', error: rowWriteError };
            }
          }

          try {
            await deps.afterSuccessfulWrite();
          } catch {
            /* the write landed; consumers refresh on their next fetch */
          }
          return { ok: true };
        }
      }

      let nextKeys = applyPerPlatformPatches(freshKeys, resolvedEntries);
      if (input.transformKeys) nextKeys = input.transformKeys(nextKeys);

      let writeError: unknown = null;
      try {
        const { error } = await deps.persistUpdate(postIdAtEnqueue, {
          ...(input.extraUpdates ?? {}),
          media_keys: nextKeys,
        });
        writeError = error ?? null;
      } catch (error) {
        writeError = error;
      }
      if (writeError != null) {
        revertIfSamePost();
        return { ok: false, reason: 'write-failed', error: writeError };
      }

      try {
        await deps.afterSuccessfulWrite();
      } catch {
        /* the write landed; consumers refresh on their next fetch */
      }
      return { ok: true };
    };

    // Serialize: run N+1 starts only after run N settles — success OR failure
    // (the second `run` argument keeps the chain alive past rejections, though
    // runs themselves return failure results rather than throwing).
    const result = chain.then(run, run);
    chain = result;
    return result;
  }

  return { enqueue };
}

/**
 * Per-platform in-flight commit counting (finding #6) — pure helpers for the
 * `Record<platform, count>` map behind the pickers' isSaving indicator, so a
 * commit for platform A never shows platform B's picker as saving. Counts
 * (not booleans) because queued commits overlap: two in-flight commits for
 * the same platform must keep the indicator on until BOTH settle.
 */
export function incrementSaving(
  map: Record<string, number>,
  platforms: string[],
): Record<string, number> {
  if (platforms.length === 0) return map;
  const next = { ...map };
  for (const p of platforms) next[p] = (next[p] ?? 0) + 1;
  return next;
}

export function decrementSaving(
  map: Record<string, number>,
  platforms: string[],
): Record<string, number> {
  if (platforms.length === 0) return map;
  const next = { ...map };
  for (const p of platforms) {
    const count = (next[p] ?? 0) - 1;
    if (count <= 0) delete next[p];
    else next[p] = count;
  }
  return next;
}
